import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { delay } from '~/common/utils/async.util';
import { getNoticeDetailApi } from '~/features/notices/api/get-notice-detail.api';
import { getNoticesApi } from '~/features/notices/api/get-notices.api';
import { NoticeType } from '~/features/notices/types/notices.type';

import type { Nullable } from '~/common/types/nullable.type';
import type { PaginationMeta } from '~/common/types/pagination.type';
import type {
  NoticeDetailItem,
  NoticeListItem,
  NoticeListParams,
} from '~/features/notices/types/notices.type';

export interface FaqState {
  isLoading: boolean;
  isLoadingDetail: boolean;
  items: NoticeListItem[];
  meta: Nullable<PaginationMeta>;
  detail: Nullable<NoticeDetailItem>;
}

const initialState: FaqState = {
  isLoading: false,
  isLoadingDetail: false,
  items: [],
  meta: null,
  detail: null,
};

/**
 * FAQ 목록 조회
 */
export const fetchFaqs = createAsyncThunk(
  'faq/fetchFaqs',
  async (params: Omit<NoticeListParams, 'type'> | undefined, { rejectWithValue }) => {
    try {
      const [{ data }] = await Promise.all([
        getNoticesApi({
          page: 1,
          ...params,
          type: [NoticeType.FAQ],
        }),
        delay(200),
      ]);
      return data;
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

/**
 * FAQ 상세 조회
 */
export const fetchFaqDetail = createAsyncThunk(
  'faq/fetchFaqDetail',
  async (id: number, { rejectWithValue }) => {
    try {
      const { data } = await getNoticeDetailApi(id);
      return data;
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

const FaqSlice = createSlice({
  name: 'faq',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(fetchFaqs.pending, state => {
        state.isLoading = true;
      })
      .addCase(fetchFaqs.fulfilled, (state, action) => {
        state.items = action.payload.data;
        state.meta = action.payload.meta;
        state.isLoading = false;
      })
      .addCase(fetchFaqs.rejected, state => {
        state.isLoading = false;
        state.items = [];
        state.meta = null;
      });

    builder
      .addCase(fetchFaqDetail.pending, state => {
        state.isLoadingDetail = true;
      })
      .addCase(fetchFaqDetail.fulfilled, (state, action) => {
        state.detail = action.payload.data;
        state.isLoadingDetail = false;
      })
      .addCase(fetchFaqDetail.rejected, state => {
        state.detail = null;
        state.isLoadingDetail = false;
      });
  },
});

export default FaqSlice;
